// Copyright (c) 2026 Isolated Networks. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use
// of this file, via any medium, is strictly prohibited.
// SPDX-License-Identifier: LicenseRef-Proprietary

import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { brand, brandFontFamily, brandRgb, designTokens } from '../theme/designTokens';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeContextProvider');
  }
  return context;
};

const cyan = brand.cyan;
const navy = brand.navy;
const sky = brand.sky;
const gray = brand.gray;
const grayMid = brand.grayMid;
const grayLight = brand.grayLight;

// Logo guide typography: Maven Pro — ExtraBold 800 / Bold 700 / Regular 400
const sharedTypography = {
  fontFamily: brandFontFamily,
  h1: {
    fontSize: '2.5rem',
    fontWeight: 800,
    letterSpacing: '-0.02em',
    lineHeight: 1.1,
    textWrap: 'balance',
  },
  h2: {
    fontSize: '2rem',
    fontWeight: 800,
    letterSpacing: '-0.02em',
    lineHeight: 1.15,
    textWrap: 'balance',
  },
  h3: {
    fontSize: '1.75rem',
    fontWeight: 700,
    letterSpacing: '-0.015em',
    lineHeight: 1.2,
    textWrap: 'balance',
  },
  h4: {
    fontSize: '1.5rem',
    fontWeight: 700,
    letterSpacing: '-0.015em',
    lineHeight: 1.25,
    textWrap: 'balance',
  },
  h5: {
    fontSize: '1.25rem',
    fontWeight: 700,
    letterSpacing: '-0.01em',
    lineHeight: 1.3,
  },
  h6: {
    fontSize: '1rem',
    fontWeight: 700,
    letterSpacing: '-0.01em',
    lineHeight: 1.35,
  },
  subtitle1: {
    fontSize: '1rem',
    fontWeight: 700,
    letterSpacing: '0.005em',
    lineHeight: 1.5,
  },
  subtitle2: {
    fontSize: '0.875rem',
    fontWeight: 700,
    letterSpacing: '0.005em',
    lineHeight: 1.5,
  },
  body1: {
    fontWeight: 400,
    lineHeight: 1.6,
    letterSpacing: '0.01em',
  },
  body2: {
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: '0.01em',
  },
  button: {
    fontWeight: 700,
    letterSpacing: '0.02em',
  },
  overline: {
    fontWeight: 700,
    letterSpacing: '0.08em',
    fontSize: '0.7rem',
  },
};

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: cyan,
      light: sky,
      dark: navy,
      contrastText: brand.offWhite,
    },
    secondary: {
      main: gray,
      light: grayMid,
      dark: '#3f4042',
      contrastText: brand.offWhite,
    },
    background: {
      default: brand.ink,
      paper: brand.surface,
    },
    text: {
      primary: brand.offWhite,
      secondary: grayLight,
    },
    error: {
      main: brand.error,
      light: '#f2555a',
      dark: '#c73c40',
    },
    warning: {
      main: brand.warning,
      light: '#f0b942',
      dark: '#c48a12',
    },
    info: {
      main: cyan,
    },
    success: {
      main: brand.success,
      light: '#3cb97c',
      dark: '#268a59',
    },
    divider: `rgba(${brandRgb.sky}, 0.14)`,
  },
  typography: sharedTypography,
  shape: {
    borderRadius: 6,
  },
  shadows: [
    'none',
    '0 1px 2px rgba(11, 12, 24, 0.35)',
    '0 1px 4px rgba(11, 12, 24, 0.4)',
    '0 2px 6px rgba(11, 12, 24, 0.4)',
    '0 2px 8px rgba(11, 12, 24, 0.45)',
    '0 3px 10px rgba(11, 12, 24, 0.45)',
    '0 4px 12px rgba(11, 12, 24, 0.45)',
    '0 5px 15px rgba(11, 12, 24, 0.5)',
    '0 6px 18px rgba(11, 12, 24, 0.5), 0 2px 6px rgba(33, 30, 113, 0.2)',
    '0 8px 22px rgba(11, 12, 24, 0.55), 0 3px 8px rgba(33, 30, 113, 0.2)',
    '0 10px 28px rgba(11, 12, 24, 0.55), 0 4px 10px rgba(33, 30, 113, 0.25)',
    '0 12px 32px rgba(11, 12, 24, 0.55), 0 4px 12px rgba(33, 30, 113, 0.25)',
    '0 14px 36px rgba(11, 12, 24, 0.6), 0 5px 14px rgba(33, 30, 113, 0.25)',
    '0 16px 40px rgba(11, 12, 24, 0.6), 0 6px 16px rgba(33, 30, 113, 0.25)',
    '0 18px 44px rgba(11, 12, 24, 0.65), 0 6px 18px rgba(33, 30, 113, 0.25)',
    '0 20px 48px rgba(11, 12, 24, 0.65), 0 7px 20px rgba(33, 30, 113, 0.3)',
    '0 22px 52px rgba(11, 12, 24, 0.65), 0 8px 22px rgba(33, 30, 113, 0.3)',
    '0 24px 56px rgba(11, 12, 24, 0.7), 0 8px 24px rgba(33, 30, 113, 0.3)',
    '0 26px 60px rgba(11, 12, 24, 0.7), 0 9px 26px rgba(33, 30, 113, 0.3)',
    '0 28px 64px rgba(11, 12, 24, 0.75), 0 10px 28px rgba(33, 30, 113, 0.3)',
    '0 30px 68px rgba(11, 12, 24, 0.75), 0 10px 30px rgba(33, 30, 113, 0.3)',
    '0 32px 72px rgba(11, 12, 24, 0.75), 0 11px 32px rgba(33, 30, 113, 0.3)',
    '0 34px 76px rgba(11, 12, 24, 0.8), 0 12px 34px rgba(33, 30, 113, 0.35)',
    '0 36px 80px rgba(11, 12, 24, 0.8), 0 12px 36px rgba(33, 30, 113, 0.35)',
    '0 40px 90px rgba(11, 12, 24, 0.85), 0 14px 40px rgba(33, 30, 113, 0.35)',
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ':root': {
          '--in-navy': navy,
          '--in-cyan': cyan,
          '--in-gray': gray,
          '--in-gray-mid': grayMid,
          '--in-sky': sky,
          '--in-gray-light': grayLight,
          '--in-ink': brand.ink,
          '--in-surface': brand.surface,
          '--in-elevated': brand.elevated,
          '--in-font': brandFontFamily,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: brand.ink,
          boxShadow: `0 1px 3px rgba(11, 12, 24, 0.5), inset 0 -1px 0 rgba(${brandRgb.cyan}, 0.12)`,
          backdropFilter: 'blur(12px)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: brand.ink,
          borderRight: `1px solid rgba(${brandRgb.sky}, 0.1)`,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: brand.surface,
          borderRadius: 8,
          border: `1px solid rgba(${brandRgb.sky}, 0.1)`,
          boxShadow: '0 2px 8px rgba(11, 12, 24, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.02)',
          transition: 'box-shadow 0.25s cubic-bezier(0.16, 1, 0.3, 1), border-color 0.25s ease',
          '&:hover': {
            boxShadow: '0 6px 20px rgba(11, 12, 24, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.03)',
            borderColor: `rgba(${brandRgb.cyan}, 0.22)`,
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          textTransform: 'none',
          padding: '8px 18px',
          fontWeight: 700,
          boxShadow: 'none',
          transition: 'all 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
          '&:hover': {
            boxShadow: '0 2px 8px rgba(11, 12, 24, 0.3)',
          },
        },
        containedPrimary: {
          background: `linear-gradient(135deg, ${cyan} 0%, ${navy} 100%)`,
          boxShadow: `0 1px 3px rgba(${brandRgb.cyan}, 0.35)`,
          '&:hover': {
            background: `linear-gradient(135deg, #1aadef 0%, #2a2785 100%)`,
            boxShadow: `0 3px 10px rgba(${brandRgb.cyan}, 0.4)`,
          },
        },
        outlinedPrimary: {
          borderColor: `rgba(${brandRgb.cyan}, 0.55)`,
          '&:hover': {
            borderColor: cyan,
            background: `rgba(${brandRgb.cyan}, 0.08)`,
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 6,
            transition: 'box-shadow 0.2s cubic-bezier(0.16, 1, 0.3, 1), border-color 0.2s ease',
            '&.Mui-focused': {
              boxShadow: `0 0 0 3px rgba(${brandRgb.cyan}, 0.18)`,
            },
            '&.Mui-error': {
              boxShadow: 'none',
              '&.Mui-focused': {
                boxShadow: '0 0 0 3px rgba(229, 72, 77, 0.15)',
              },
            },
          },
          '& .MuiFormHelperText-root': {
            marginTop: 6,
            fontSize: '0.75rem',
            lineHeight: 1.4,
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        outlined: {
          borderRadius: 6,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid rgba(${brandRgb.sky}, 0.1)`,
          fontFeatureSettings: '"tnum"',
        },
        head: {
          fontWeight: 700,
          backgroundColor: `rgba(${brandRgb.navy}, 0.35)`,
          fontSize: '0.8rem',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: grayLight,
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          transition: 'background-color 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
          '&.Mui-selected': {
            backgroundColor: `rgba(${brandRgb.cyan}, 0.14)`,
            borderLeft: `3px solid ${cyan}`,
          },
          '&.Mui-selected:hover': {
            backgroundColor: `rgba(${brandRgb.cyan}, 0.2)`,
          },
          '&:hover': {
            backgroundColor: `rgba(${brandRgb.sky}, 0.06)`,
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: `rgba(${brandRgb.sky}, 0.1)`,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
        },
        outlinedPrimary: {
          borderColor: `rgba(${brandRgb.cyan}, 0.45)`,
        },
        sizeSmall: {
          fontSize: '0.75rem',
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: cyan,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: 'rgba(26, 29, 54, 0.94)',
          backdropFilter: 'blur(12px)',
          border: `1px solid rgba(${brandRgb.sky}, 0.14)`,
          boxShadow: '0 4px 16px rgba(11, 12, 24, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.05)',
          fontSize: '0.75rem',
          borderRadius: 8,
          padding: '8px 12px',
        },
        arrow: {
          color: 'rgba(26, 29, 54, 0.94)',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          border: `1px solid rgba(${brandRgb.sky}, 0.12)`,
          boxShadow:
            '0 24px 80px rgba(11, 12, 24, 0.7), 0 8px 24px rgba(33, 30, 113, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.03)',
          backgroundImage: 'none',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 700,
        },
        standardError: {
          backgroundColor: 'rgba(229, 72, 77, 0.08)',
          border: '1px solid rgba(229, 72, 77, 0.18)',
          '& .MuiAlert-icon': { color: brand.error },
        },
        standardWarning: {
          backgroundColor: 'rgba(232, 163, 23, 0.08)',
          border: '1px solid rgba(232, 163, 23, 0.18)',
          '& .MuiAlert-icon': { color: brand.warning },
        },
        standardInfo: {
          backgroundColor: `rgba(${brandRgb.cyan}, 0.1)`,
          border: `1px solid rgba(${brandRgb.cyan}, 0.22)`,
          '& .MuiAlert-icon': { color: cyan },
        },
        standardSuccess: {
          backgroundColor: 'rgba(48, 164, 108, 0.08)',
          border: '1px solid rgba(48, 164, 108, 0.18)',
          '& .MuiAlert-icon': { color: brand.success },
        },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: {
          backgroundColor: `rgba(${brandRgb.sky}, 0.06)`,
          borderRadius: 6,
          '&::after': {
            background: `linear-gradient(90deg, transparent, rgba(${brandRgb.sky}, 0.08), transparent)`,
          },
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(11, 12, 24, 0.35)',
        },
        invisible: {
          backgroundColor: 'transparent',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 700,
          letterSpacing: '0.01em',
        },
      },
    },
  },
});

const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: navy,
      light: cyan,
      dark: '#18165a',
      contrastText: brand.white,
    },
    secondary: {
      main: brand.partnerGreen,
      light: brand.partnerGreenLight,
      dark: brand.partnerGreenDark,
      contrastText: brand.white,
    },
    background: {
      default: '#F4F6F9',
      paper: brand.white,
    },
    text: {
      primary: '#1c2028',
      secondary: grayMid,
    },
    error: {
      main: '#d93036',
    },
    warning: {
      main: '#d69e11',
    },
    info: {
      main: cyan,
    },
    success: {
      main: '#288a54',
    },
    divider: 'rgba(28, 32, 40, 0.1)',
  },
  typography: {
    ...sharedTypography,
    h1: { ...sharedTypography.h1, color: navy },
    h2: { ...sharedTypography.h2, color: navy },
    h3: { ...sharedTypography.h3, color: navy },
    h4: { ...sharedTypography.h4, color: '#1c2028' },
    h5: { ...sharedTypography.h5, color: '#1c2028' },
    h6: { ...sharedTypography.h6, color: '#1c2028' },
  },
  shape: {
    borderRadius: 6,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ':root': {
          '--in-navy': navy,
          '--in-cyan': cyan,
          '--in-gray': gray,
          '--in-gray-mid': grayMid,
          '--in-sky': sky,
          '--in-gray-light': grayLight,
          '--in-ink': brand.ink,
          '--in-surface': brand.surface,
          '--in-elevated': brand.elevated,
          '--in-font': brandFontFamily,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: brand.white,
          color: navy,
          boxShadow: '0 1px 3px rgba(33, 30, 113, 0.08), inset 0 -1px 0 rgba(33, 30, 113, 0.08)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          background: `linear-gradient(180deg, ${brand.partnerGreen} 0%, ${brand.partnerGreenDark} 100%)`,
          borderRight: '1px solid rgba(255, 255, 255, 0.2)',
          color: brand.white,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: brand.white,
          borderRadius: 8,
          border: '1px solid rgba(33, 30, 113, 0.08)',
          boxShadow: '0 1px 4px rgba(33, 30, 113, 0.06), 0 4px 12px rgba(33, 30, 113, 0.04)',
          transition: 'box-shadow 0.25s cubic-bezier(0.16, 1, 0.3, 1), border-color 0.25s ease',
          '&:hover': {
            boxShadow: '0 4px 16px rgba(33, 30, 113, 0.1), 0 8px 24px rgba(0, 152, 223, 0.08)',
            borderColor: `rgba(${brandRgb.cyan}, 0.35)`,
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          textTransform: 'none',
          padding: '8px 18px',
          fontWeight: 700,
          boxShadow: 'none',
          transition: 'all 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
          '&:hover': {
            boxShadow: '0 2px 6px rgba(33, 30, 113, 0.12)',
          },
        },
        containedPrimary: {
          background: `linear-gradient(135deg, ${brand.partnerGreen} 0%, ${brand.partnerGreenDark} 100%)`,
          color: brand.white,
          boxShadow: '0 1px 3px rgba(106, 176, 76, 0.3)',
          '&:hover': {
            background: 'linear-gradient(135deg, #76bc58 0%, #5fa843 100%)',
            boxShadow: '0 3px 10px rgba(106, 176, 76, 0.3)',
          },
        },
        outlinedPrimary: {
          borderColor: `rgba(${brandRgb.navy}, 0.45)`,
          color: navy,
          '&:hover': {
            borderColor: navy,
            background: `rgba(${brandRgb.navy}, 0.06)`,
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 6,
            backgroundColor: brand.white,
            transition: 'box-shadow 0.2s ease',
            '&.Mui-focused': {
              boxShadow: `0 0 0 2px rgba(${brandRgb.cyan}, 0.2)`,
            },
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid rgba(33, 30, 113, 0.08)',
          fontFeatureSettings: '"tnum"',
        },
        head: {
          fontWeight: 700,
          backgroundColor: `rgba(${brandRgb.sky}, 0.35)`,
          fontSize: '0.8rem',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: grayMid,
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          '&.Mui-selected': {
            backgroundColor: `rgba(${brandRgb.cyan}, 0.1)`,
            borderLeft: `3px solid ${cyan}`,
          },
          '&.Mui-selected:hover': {
            backgroundColor: `rgba(${brandRgb.cyan}, 0.16)`,
          },
          '&:hover': {
            backgroundColor: 'rgba(33, 30, 113, 0.04)',
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(33, 30, 113, 0.1)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: brand.white,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 700,
        },
        outlinedPrimary: {
          borderColor: `rgba(${brandRgb.navy}, 0.4)`,
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: navy,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: navy,
          boxShadow: '0 4px 12px rgba(33, 30, 113, 0.25)',
          fontSize: '0.75rem',
          borderRadius: 6,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          border: '1px solid rgba(33, 30, 113, 0.1)',
          boxShadow: '0 20px 60px rgba(33, 30, 113, 0.15)',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 700,
          letterSpacing: '0.01em',
        },
      },
    },
  },
});

export const ThemeContextProvider = ({ children }) => {
  const getInitialTheme = () => {
    const savedTheme = localStorage.getItem('theme-mode');
    if (savedTheme) {
      return savedTheme === 'dark';
    }
    return true;
  };

  const [isDarkMode, setIsDarkMode] = useState(getInitialTheme);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const syncTheme = async () => {
      try {
        const response = await fetch('/api/theme/current');
        if (response.ok) {
          const data = await response.json();
          if (data.is_dark_mode !== isDarkMode) {
            setIsDarkMode(data.is_dark_mode);
            localStorage.setItem('theme-mode', data.is_dark_mode ? 'dark' : 'light');
          }
        }
      } catch (error) {
        console.error('Failed to sync theme with server:', error);
      }
    };

    syncTheme();
  }, []);

  useEffect(() => {
    if (!loading) {
      localStorage.setItem('theme-mode', isDarkMode ? 'dark' : 'light');
    }
  }, [isDarkMode, loading]);

  const toggleTheme = useCallback(() => {
    console.warn('Direct theme toggling is disabled. Use the theme configuration dialog.');
  }, []);

  const currentTheme = isDarkMode ? darkTheme : lightTheme;

  const themeContextValue = useMemo(
    () => ({
      isDarkMode,
      toggleTheme,
      setIsDarkMode,
      brand,
      designTokens,
    }),
    [isDarkMode, toggleTheme]
  );

  return (
    <ThemeContext.Provider value={themeContextValue}>
      <ThemeProvider theme={currentTheme}>{children}</ThemeProvider>
    </ThemeContext.Provider>
  );
};
